import { Injectable, computed, effect, inject, linkedSignal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { type Observable, tap } from 'rxjs';

import { AccessService } from '../access/access.service';
import { AuthStore } from '../auth/auth.store';
import { StorageKey } from '../storage/storage.keys';
import { StorageService } from '../storage/storage.service';
import { NO_PROJECT, NO_PROJECT_ID, type Project, type ProjectInput } from './project.model';
import { ProjectsApi } from './projects.api';

/**
 * The project the Projects pane remembers. The session JWT has no selected
 * project; listing is scoped by workplace.
 *
 * Project pages take their project from the URL (`/projects/:projectId/…`)
 * and write it back here, so the pane opens on the last project used.
 * Workspace-level pages (`/tasker`, `/scheduler`) never read it: they always
 * act in the workspace's default project.
 *
 * Selection is persisted in localStorage under
 * `{@link StorageKey.SELECTED_PROJECT}:{companyId}:{branchId}` so a reload
 * restores the same project for that workplace. An empty id means
 * {@link NO_PROJECT}: nothing picked yet.
 */
@Injectable({ providedIn: 'root' })
export class ProjectSelectionStore {
  private readonly api = inject(ProjectsApi);
  private readonly session = inject(AuthStore);
  private readonly access = inject(AccessService);
  private readonly storage = inject(StorageService);

  private readonly workspace = computed((): string | undefined => {
    const user = this.session.user();

    if (!user?.selectedCompanyId || !this.access.can('settings:manage')) {
      return undefined;
    }

    return `${user.selectedCompanyId}:${user.selectedBranchId ?? ''}`;
  });

  private readonly listRes = rxResource({
    params: this.workspace,
    stream: () => this.api.list(),
  });

  readonly projects = computed((): readonly Project[] | undefined =>
    this.listRes.hasValue() ? this.listRes.value() : undefined,
  );

  readonly hasError = computed(() => this.listRes.error() !== undefined);

  /**
   * On workplace change (and on first authenticated render), reloads the id
   * previously written by {@link select}. Missing storage → {@link NO_PROJECT_ID}.
   */
  private readonly selectedId = linkedSignal<string | undefined, string>({
    source: this.workspace,
    computation: (workspace) => {
      if (!workspace) {
        return NO_PROJECT_ID;
      }

      return this.storage.read(this.storageKey(workspace)) ?? NO_PROJECT_ID;
    },
  });

  /** Holds a pick that is not in `projects` yet (e.g. just created, list reloading). */
  private readonly pendingSelected = signal<Project | null>(null);

  constructor() {
    effect(() => {
      this.workspace();
      this.pendingSelected.set(null);
    });
  }

  /** Empty string when {@link NO_PROJECT} is active. */
  readonly selectedProjectId = computed((): string => this.selected().id);

  readonly selected = computed((): Project => {
    const id = this.selectedId();

    if (id === NO_PROJECT_ID) {
      return NO_PROJECT;
    }

    const list = this.projects() ?? [];
    const fromList = list.find((project) => project.id === id);

    if (fromList) {
      return fromList;
    }

    const pending = this.pendingSelected();

    if (pending !== null && pending.id === id) {
      return pending;
    }

    return NO_PROJECT;
  });

  /** True once the caller may list (and so pick) the workspace's projects. */
  readonly canSelect = computed(() => this.workspace() !== undefined);

  /** Make `project` the active one and persist its id for this workplace. */
  select(project: Project): void {
    if (project.id === this.selectedId() && project.id === this.selected().id) {
      return;
    }

    const workspace = this.workspace();

    this.selectedId.set(project.id);
    this.pendingSelected.set(project.id === NO_PROJECT_ID ? null : project);

    if (workspace) {
      this.storage.write(this.storageKey(workspace), project.id);
    }
  }

  /** Create a project, select it (and persist), then refresh the list. */
  create(input: ProjectInput): Observable<Project> {
    return this.api.create(input).pipe(
      tap((project) => {
        this.select(project);
        this.listRes.reload();
      }),
    );
  }

  reload(): void {
    this.listRes.reload();
  }

  /** Replace name/dates, keep selection on the updated row, then refresh. */
  update(id: string, input: ProjectInput): Observable<Project> {
    return this.api.update(id, input).pipe(
      tap((project) => {
        this.select(project);
        this.listRes.reload();
      }),
    );
  }

  /** Soft-fail after delete: fall back to {@link NO_PROJECT}, then refresh. */
  remove(id: string): Observable<void> {
    return this.api.delete(id).pipe(
      tap(() => {
        if (this.selectedId() === id) {
          this.select(NO_PROJECT);
        }

        this.listRes.reload();
      }),
    );
  }

  private storageKey(workspace: string): string {
    return `${StorageKey.SELECTED_PROJECT}:${workspace}`;
  }
}
